import { isProPlan } from '../lib/plans';
import { capacityText } from '../lib/capacity';
import SpeechInput from '../components/SpeechInput';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import RequestError from '../components/RequestError';
import { Link } from 'react-router-dom';
import {
  Sparkles, Send, Plus, Trash2, MapPin, ArrowRight, MessageCircle, Bot, Globe, ThumbsUp, CheckCircle2,
} from 'lucide-react';
import { MATCH_FEATURE_NAME } from '../config/brand';
import { PlanQuotaBadge, usePlan } from '../components/PlanBadges';
import { apiErrorMessage } from '../lib/apiError';
import { evidenceLines, isExternalCandidate, safePublicUrl } from '../components/CandidateCard';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  'Bu yıl İstanbul\'da hangi etkinlik türleri trend?',
  '30 kişilik bir seramik atölyesi fikrini geliştirmek istiyorum',
  'Kurumsal bir tanıtım etkinliği için yaratıcı konsept öner',
  'Kadıköy\'de hafta sonu 50 kişilik bir workshop için mekân bul',
];

const OPEN_STATUSES = new Set(['new', 'admin_reviewing', 'contacted']);

// A7: dış kartta uygulama içi ilgi/başvuru yok (EXT-08); doğrulanmış herkese açık bağlantı gösterilir.
const ExternalMiniCard = ({ v }) => {
  const url = safePublicUrl(v.cta?.url);
  return <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid={`assistant-venue-${v.id}`}>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2459c4]">{v.external_label || 'Dış Mekân'}</p>
    <p className="font-semibold text-sm text-zinc-900">{v.name}</p>
    <p className="text-xs text-zinc-500 mt-0.5">{v.semt || 'İlçe net değil'} · {v.capacity?.text || 'Net değil'}</p>
    <p className="text-xs text-zinc-500 mt-1">Potansiyel seçenek; görüşülmesi gerekir. Uygulama içinden teklif gönderilmez.</p>
    {url && <a href={url} target="_blank" rel="noopener noreferrer nofollow" className="mt-2 inline-flex text-xs font-semibold text-[#1E3A8A] underline" data-testid={`assistant-venue-public-link-${v.id}`}>{v.cta?.label || 'Bağlantıyı aç'}</a>}
  </div>;
};

const VenueMiniCard = ({ v, applied, busy, onInterest }) => isExternalCandidate(v) ? <ExternalMiniCard v={v} /> : (
  <div className="bg-white border border-zinc-200 hover:border-[#1E3A8A] rounded-xl p-3 transition-colors group" data-testid={`assistant-venue-${v.id}`}>
    <Link to={`/venues/${v.id}`} className="flex gap-3" data-testid={`assistant-venue-link-${v.id}`}>
      {v.image ? (
        <img src={v.image} alt={v.name} className="w-16 h-16 object-cover rounded-lg bg-zinc-100 shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-[#E6EEFF] flex items-center justify-center shrink-0">
          <MapPin size={20} strokeWidth={1.5} className="text-[#1E3A8A]/50" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-sm text-zinc-900 truncate group-hover:text-[#1E3A8A]">{v.name}</p>
        </div>
        <p className="text-xs text-zinc-500 mt-0.5">{v.semt}{v.mahalle ? ` · ${v.mahalle}` : ''} · {capacityText(v.min_capacity, v.max_capacity)}</p>
        {(evidenceLines(v, 1)[0]) && (
          <p className="text-xs text-emerald-700 mt-1 truncate">✓ {evidenceLines(v, 1)[0]}</p>
        )}
      </div>
      <ArrowRight size={15} strokeWidth={2} className="text-zinc-300 group-hover:text-[#1E3A8A] self-center shrink-0" />
    </Link>
    <button
      type="button"
      onClick={() => onInterest(v)}
      disabled={busy || applied}
      data-testid={`assistant-venue-interest-btn-${v.id}`}
      className={`mt-2.5 w-full h-9 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
        applied
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
          : 'bg-[#1E3A8A] text-white hover:bg-[#16307A] disabled:opacity-60'
      }`}
    >
      {applied
        ? (<><CheckCircle2 size={14} strokeWidth={2} /> Başvurun alındı</>)
        : (<><ThumbsUp size={14} strokeWidth={2} /> {busy ? 'Kaydediliyor…' : 'Bu mekânla ilgileniyorum'}</>)}
    </button>
  </div>
);

const renderInline = (text, keyPrefix) => {
  const parts = String(text).split(/(\[[^\]]+\]\((?:https?:\/\/)[^)\s]+\))/g);
  return parts.map((p, i) => {
    const link = p.match(/^\[([^\]]+)\]\(((?:https?:\/\/)[^)\s]+)\)$/);
    if (link) {
      return (
        <a key={`${keyPrefix}-${i}`} href={link[2]} target="_blank" rel="noopener noreferrer"
           className="text-[#1E3A8A] underline underline-offset-2 hover:text-[#16307A] break-all">
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>;
  });
};

const renderContent = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{renderInline(p.slice(2, -2), `b${i}`)}</strong>
      : <React.Fragment key={i}>{renderInline(p, `t${i}`)}</React.Fragment>
  );
};

const Message = ({ m, prevUserText, interested, busyId, onInterest }) => {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end" data-testid={`assistant-msg-user-${m.id}`}>
        <div className="max-w-[80%] bg-[#1E3A8A] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap">
          {m.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5" data-testid={`assistant-msg-ai-${m.id}`}>
      <div className="w-8 h-8 rounded-full bg-[#E6EEFF] flex items-center justify-center shrink-0 mt-0.5">
        <Bot size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />
      </div>
      <div className="max-w-[85%] space-y-2">
        <div className="bg-white border border-zinc-100 rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-zinc-800 whitespace-pre-wrap">
          {renderContent(m.content)}
        </div>
        {m.sources?.length > 0 && (
          <div className="bg-[#F8FAFF] border border-[#E6EEFF] rounded-xl px-3.5 py-2.5" data-testid={`assistant-sources-${m.id}`}>
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-1 mb-1.5">
              <Globe size={11} strokeWidth={2} /> Kaynaklar
            </p>
            <ol className="space-y-1">
              {m.sources.map((s, i) => (
                <li key={s.url} className="text-xs flex gap-1.5">
                  <span className="text-zinc-400 shrink-0">{i + 1}.</span>
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                     className="text-[#1E3A8A] hover:underline break-all"
                     data-testid={`assistant-source-link-${m.id}-${i}`}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}
        {m.venues?.length > 0 && (
          <div className="space-y-2" data-testid={`assistant-venues-${m.id}`}>
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-1">
              <MapPin size={11} strokeWidth={2} /> {MATCH_FEATURE_NAME} motorunun bulduğu mekânlar
            </p>
            {m.venues.map(v => (
              <VenueMiniCard
                key={v.id}
                v={v}
                applied={interested.has(v.id)}
                busy={busyId === v.id}
                onInterest={(venue) => onInterest(venue, m, prevUserText)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OrganizerAssistant = () => {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [quotaTick, setQuotaTick] = useState(0);
  const plan = usePlan(quotaTick);
  const canChat = isProPlan(plan?.plan);
  const bottomRef = useRef(null);
  const skipLoadRef = useRef(false);
  const [interested, setInterested] = useState(() => new Set());
  const [busyVenueId, setBusyVenueId] = useState(null);

  const loadInterests = async () => {
    try {
      const { data } = await axios.get(`${API}/api/organizer/interests`);
      setInterested(new Set(data.filter(i => OPEN_STATUSES.has(i.status)).map(i => i.venue_id)));
    } catch { /* ignore */ }
  };

  const submitInterest = async (venue, assistantMsg) => {
    if (interested.has(venue.id) || busyVenueId) return;
    setBusyVenueId(venue.id);
    try {
      // A8: eski asistan sayfası da yapılandırılmış teklif gönderir; kullanıcının serbest yazısı mekâna gitmez
      // (sunucu dolu free_text/note için 422 OLD_FLOW_UNSUPPORTED döner). Metni sunucu kriterlerden üretir.
      await axios.post(`${API}/api/organizer/interests`, {
        venue_id: venue.id,
        applied_filters: assistantMsg.applied_filters || null,
      });
      setInterested(prev => new Set(prev).add(venue.id));
      toast.success('Hazır teklif mekâna iletildi. Yanıtı Taleplerim’den takip edebilirsin.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Başvuru kaydedilemedi.');
      if (err.response?.status === 409 && !(detail?.code || '').endsWith('_LIMIT')) {
        setInterested(prev => new Set(prev).add(venue.id));
        toast.warning('Bu mekâna zaten açık bir başvurun var.');
      } else {
        toast.error(message);
      }
    } finally {
      setBusyVenueId(null);
    }
  };

  const loadSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/organizer/assistant/sessions`);
      setSessions(data);
      return data;
    } catch (err) {
      setRequestError(apiErrorMessage(err, 'Sohbetler yüklenemedi.'));
      return [];
    }
  };

  useEffect(() => { loadSessions(); loadInterests(); }, []);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    if (skipLoadRef.current) { skipLoadRef.current = false; return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/organizer/assistant/sessions/${activeId}/messages`);
        setMessages(data);
      } catch { toast.error('Sohbet yüklenemedi.'); }
    })();
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if (!canChat || !trimmed || sending || voiceBusy) return;
    setRequestError('');
    setSending(true);
    setInput('');
    let sid = activeId;
    try {
      if (!sid) {
        const { data: s } = await axios.post(`${API}/api/organizer/assistant/sessions`);
        sid = s.id;
        skipLoadRef.current = true;
        setActiveId(sid);
      }
      setMessages(prev => [...prev, { id: `tmp-${Date.now()}`, role: 'user', content: trimmed, venues: [] }]);
      const { data } = await axios.post(`${API}/api/organizer/assistant/sessions/${sid}/messages`, { text: trimmed });
      setMessages(prev => [...prev.filter(m => !String(m.id).startsWith('tmp-')), data.user_message, data.assistant_message]);
      loadSessions();
    } catch (err) {
      const message = apiErrorMessage(err, 'Mesaj gönderilemedi.');
      setRequestError(message);
      toast.error(message);
      setMessages(prev => prev.filter(m => !String(m.id).startsWith('tmp-')));
      setInput(trimmed);
    } finally {
      setSending(false); setQuotaTick(t => t + 1);
    }
  };

  const removeSession = async (e, sid) => {
    e.stopPropagation();
    if (!window.confirm('Bu sohbeti silmek istediğine emin misin?')) return;
    try {
      await axios.delete(`${API}/api/organizer/assistant/sessions/${sid}`);
      if (sid === activeId) setActiveId(null);
      loadSessions();
    } catch { toast.error('Silinemedi.'); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FAFAFA]">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 flex flex-col md:flex-row gap-4 h-[calc(100vh-6rem)]">

        {/* Sessions sidebar */}
        <aside className="md:w-60 shrink-0 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto" data-testid="assistant-sessions">
          <button
            onClick={() => setActiveId(null)}
            data-testid="assistant-new-chat"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1E3A8A] hover:bg-[#16307A] text-white rounded-xl text-sm font-medium transition-colors shrink-0 justify-center"
          >
            <Plus size={16} strokeWidth={2} /> Yeni Sohbet
          </button>
          {sessions.map(s => (
            <div
              key={s.id}
              onClick={() => setActiveId(s.id)}
              data-testid={`assistant-session-${s.id}`}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-colors shrink-0 max-w-52 md:max-w-none group ${
                s.id === activeId
                  ? 'bg-[#E6EEFF] text-[#1E3A8A] font-medium'
                  : 'bg-white border border-zinc-100 text-zinc-600 hover:border-zinc-300'
              }`}
            >
              <MessageCircle size={14} strokeWidth={1.75} className="shrink-0" />
              <span className="truncate flex-1">{s.title}</span>
              <button
                onClick={(e) => removeSession(e, s.id)}
                data-testid={`assistant-delete-${s.id}`}
                className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 transition-opacity shrink-0"
                aria-label="Sohbeti sil"
              >
                <Trash2 size={13} strokeWidth={1.75} />
              </button>
            </div>
          ))}
        </aside>

        {/* Chat area */}
        <div className="flex-1 flex flex-col bg-white/60 border border-zinc-100 rounded-2xl overflow-hidden min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="assistant-messages">
            {messages.length === 0 && !sending && (
              <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="assistant-empty">
                <div className="w-16 h-16 rounded-full bg-[#E6EEFF] flex items-center justify-center mb-4">
                  <Sparkles size={26} strokeWidth={1.5} className="text-[#1E3A8A]" />
                </div>
                <h2 className="text-lg font-semibold text-zinc-900 mb-1">AI Etkinlik Asistanı</h2>
                <p className="text-sm text-zinc-500 max-w-sm mb-6">
                  Etkinlik fikri iste, trendleri sor, konseptini geliştir. Hazır olduğunda uygun mekânları {MATCH_FEATURE_NAME} eşleştirme motorumuz bulur.
                </p>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {canChat && SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`assistant-suggestion-${i}`}
                      className="text-xs px-3 py-2 bg-white border border-zinc-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A] rounded-full text-zinc-600 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, idx) => (
              <Message
                key={m.id}
                m={m}
                prevUserText={[...messages.slice(0, idx)].reverse().find(x => x.role === 'user')?.content || ''}
                interested={interested}
                busyId={busyVenueId}
                onInterest={submitInterest}
              />
            ))}
            {sending && (
              <div className="flex gap-2.5" data-testid="assistant-typing">
                <div className="w-8 h-8 rounded-full bg-[#E6EEFF] flex items-center justify-center shrink-0">
                  <Bot size={16} strokeWidth={1.75} className="text-[#1E3A8A]" />
                </div>
                <div className="bg-white border border-zinc-100 rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[#1E3A8A]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-[#1E3A8A]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-[#1E3A8A]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-zinc-100 bg-white p-3">
            <RequestError message={requestError} testId="assistant-error" />
            
          {canChat ? <><SpeechInput key={activeId || 'new'} disabled={sending} onBusyChange={setVoiceBusy} onTranscript={text => setInput(prev => [prev.trim(), text].filter(Boolean).join(' ').slice(0, 4000))} />
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Etkinlik fikrini yaz ya da soru sor… (Enter ile gönder)"
                rows={1}
                data-testid="assistant-input"
                className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm resize-none focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/15 transition-all max-h-32"
              />
              <button
                onClick={() => send()}
                disabled={sending || voiceBusy || !input.trim()}
                data-testid="assistant-send"
                className="p-2.5 bg-[#1E3A8A] hover:bg-[#16307A] text-white rounded-xl transition-colors disabled:opacity-40 active:scale-95"
                aria-label="Gönder"
              >
                <Send size={18} strokeWidth={2} />
              </button>
            </div>
            </> : <p className="text-sm text-zinc-600 py-3">{plan ? <>Serbest sohbet Pro ile kullanılabilir. <Link to="/organizer/home" className="underline">Seçeneklerle çalışmaya devam et</Link> veya <Link to="/pro" className="underline">Pro özelliklerini incele</Link>.</> : 'Üyelik bilgilerin yükleniyor…'}</p>}
            <div className="mt-1.5 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[10px] text-zinc-400">
                Asistan mekân seçmez — mekânları her zaman {MATCH_FEATURE_NAME} eşleştirme motoru bulur ve sıralar.
              </p>
              <PlanQuotaBadge refreshKey={quotaTick} keys={[{ key: 'assistant_daily', label: 'mesaj' }]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizerAssistant;

